import * as React from "react";
import { CheckboxIndeterminateIcon } from "./CheckboxIndeterminateIcon";
import { ChekcboxTickIcon } from "./CheckboxTickIcon";
import {
  Box,
  ComponentThemeScaleFn,
  UnstyledButton,
  theme,
} from "@aksara-ui/react";
import { transparentize } from "polished";

export interface CheckboxContainerProps
  extends React.ComponentPropsWithoutRef<"button"> {
  errors?: boolean;
}

export const CheckboxContainer = React.forwardRef<
  HTMLButtonElement,
  CheckboxContainerProps
>(({ children, disabled, errors, ...rest }, ref) => {
  const checkboxButtonStyle: ComponentThemeScaleFn<{
    errors: boolean;
    disabled: boolean;
  }> = ({ disabled }) => ({
    backgroundColor: !disabled ? "greylight01" : "greylight02",
    width: 16,
    height: 16,
    position: "relative",
    borderRadius: "xs",
    cursor: disabled ? "not-allowed" : "pointer",
    "&:focus": {
      boxShadow: `0 0 0 2px rgba(175, 214, 255, 0.7)`,
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 2px rgba(175, 214, 255, 0.7)`,
    },
  });
  const checkboxUnselectedBoxStyle: ComponentThemeScaleFn<{
    errors: boolean;
    disabled: boolean;
  }> = ({ disabled, errors }) => ({
    backgroundColor: theme.colors.grey01,
    width: 16,
    height: 16,
    borderRadius: "xs",
    border: "1px solid",
    position: "absolute",
    top: 0,
    left: 0,
    borderColor: theme.colors.grey04,
    "&:not(:disabled)": {
      "&:hover": {
        backgroundColor: theme.colors.grey03,
        borderColor: theme.colors.grey04,
      },
    },
    ...(errors && { borderColor: "red07" }),
    ...(disabled && {
      borderColor: transparentize(0.5, theme.colors.grey05),
      backgroundColor: "greylight02",
    }),
  });
  return (
    <UnstyledButton
      ref={ref}
      type="button"
      sx={checkboxButtonStyle}
      {...rest}
      disabled={disabled}
    >
      <Box sx={checkboxUnselectedBoxStyle} />
      {children}
    </UnstyledButton>
  );
});

export interface CheckboxIndicatorProps {
  errors?: boolean;
  disabled?: boolean;
}

export const CheckboxIndicator = ({
  errors,
  disabled,
}: CheckboxIndicatorProps) => {
  const checkboxIndicatorStyle: ComponentThemeScaleFn<{
    errors: boolean;
    disabled: boolean;
  }> = ({ disabled, errors }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "blue07",
    width: 16,
    height: 16,
    borderRadius: "xs",
    color: "greylight01",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...(errors && { backgroundColor: "red07" }),
    ...(disabled && { backgroundColor: "greylight05" }),
    "&:not(:disabled)": {
      "&:hover": {
        backgroundColor: "blue08",
      },
    },
  });
  return (
    <Box sx={checkboxIndicatorStyle}>
      <ChekcboxTickIcon size={8} fill="white" />
    </Box>
  );
};

export const CheckboxIndeterminate = ({
  errors,
  disabled,
}: CheckboxIndicatorProps) => {
  const checkboxIndicatorStyle: ComponentThemeScaleFn<{
    errors: boolean;
    disabled: boolean;
  }> = ({ disabled, errors }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "blue07",
    width: 16,
    height: 16,
    borderRadius: "xs",
    color: "greylight01",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...(errors && { backgroundColor: "red07" }),
    ...(disabled && { backgroundColor: "greylight05" }),
    "&:not(:disabled)": {
      "&:hover": {
        backgroundColor: "blue08",
      },
    },
  });
  return (
    <Box sx={checkboxIndicatorStyle}>
      <CheckboxIndeterminateIcon size={8} fill="white" />
    </Box>
  );
};
